/**
 * Feeds API - Notifications
 *
 * Notification feed and stats endpoints.
 */

import { z } from 'zod'

// ============================================================================
// GET /v1/feeds/notification
// ============================================================================

export const GetNotificationFeedRequestSchema = z.object({
  user_id: z.string().optional(),
  feed_group: z.string().optional(),
  offset: z.number().min(0).optional(),
  limit: z.number().min(1).max(100).optional(),
  mark_seen: z.union([z.string(), z.boolean()]).optional(),
  mark_read: z.union([z.string(), z.boolean()]).optional(),
  id_gt: z.string().optional(),
  id_gte: z.string().optional(),
  id_lt: z.string().optional(),
  id_lte: z.string().optional()
})

export type GetNotificationFeedRequest = z.infer<typeof GetNotificationFeedRequestSchema>

/**
 * Wire shape of `GET /v1/feeds/notification`.
 *
 * The endpoint returns a GROUPED, camelCase envelope: Stream aggregates
 * notifications into groups (one per `group` key — e.g. all reactions on a
 * post) and the backend enriches + camelCases them via
 * `enrichNotificationActivities`. Each group carries its own read/seen state
 * and an `activities[]` array of the underlying activity bodies.
 *
 * This is the SINGLE SOURCE OF TRUTH for the read shape. The mobile client
 * (`apps/mobile/src/domains/notifications/api/schemas.ts`) re-exports these
 * schemas, so backend, web, and mobile validate against the same contract.
 * A divergence here is what caused SCH-313: the v3 read path emitted a flat
 * v2-style activity list, which failed mobile validation and rendered an
 * empty feed for every school after the 2026-05-14 Stream v3 cutover.
 *
 * The activity body carries a deeper graph than this contract cares about
 * (full actor / object / origin metadata owned by the unified-activity
 * store). We validate the envelope-level fields we depend on and accept
 * everything else under `looseObject`.
 */

/** Lightweight activity body — only the field the grouped envelope depends on. */
export const NotificationActivityItemSchema = z.looseObject({
  id: z.string()
})

export type NotificationActivityItem = z.infer<typeof NotificationActivityItemSchema>

/** A single grouped notification as returned by `GET /v1/feeds/notification`. */
export const NotificationItemSchema = z.looseObject({
  id: z.string(),
  isRead: z.boolean(),
  isSeen: z.boolean(),
  activityCount: z.number(),
  actorCount: z.number(),
  verb: z.string(),
  group: z.string(),
  activities: z.array(NotificationActivityItemSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export type NotificationItem = z.infer<typeof NotificationItemSchema>

export const NotificationFeedMetaSchema = z.object({
  total: z.number().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
  unread_count: z.number().optional(),
  next: z.string().nullable().optional(),
  unseen: z.number().optional(),
  unread: z.number().optional()
})

export const GetNotificationFeedResponseSchema = z.object({
  message: z.string().optional(),
  data: z.array(NotificationItemSchema),
  meta: NotificationFeedMetaSchema
})

export type GetNotificationFeedResponse = z.infer<typeof GetNotificationFeedResponseSchema>

// ============================================================================
// PATCH /v1/feeds/notification/activities
// ============================================================================

export const PatchNotificationActivitiesRequestSchema = z.object({
  feed_group: z.enum(['notification', 'app_message']).optional(),
  mark_read: z.array(z.string()).optional(),
  mark_seen: z.array(z.string()).optional()
})

export type PatchNotificationActivitiesRequest = z.infer<typeof PatchNotificationActivitiesRequestSchema>

export const PatchNotificationActivitiesResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    marked_read: z.number(),
    marked_seen: z.number(),
    total_updated: z.number()
  })
})

export type PatchNotificationActivitiesResponse = z.infer<typeof PatchNotificationActivitiesResponseSchema>

// ============================================================================
// GET /v1/feeds/:feedId/stats
// ============================================================================

export const FeedStatsRequestSchema = z.object({
  feedId: z.string().min(1),
  feedGroup: z.string().min(1)
})

export type FeedStatsRequest = z.infer<typeof FeedStatsRequestSchema>

export const FollowerInfoSchema = z.object({
  count: z.number().optional(),
  list: z.array(z.unknown()).optional()
})

export const FollowingInfoSchema = z.object({
  count: z.number().optional(),
  list: z.array(z.unknown()).optional()
})

export const FeedStatsDataSchema = z.object({
  feedId: z.string(),
  feedGroup: z.string(),
  followers: FollowerInfoSchema,
  following: FollowingInfoSchema
})

export const FeedStatsResponseSchema = z.object({
  message: z.string(),
  data: FeedStatsDataSchema
})

export type FeedStatsResponse = z.infer<typeof FeedStatsResponseSchema>
